import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import LayoutHeader from '../../layout/LayoutHeader';
import { postDataAPI, putDataAPI } from '../../utils/fetchData';
import { setErrorAlert, setSuccessAlert } from '../../store/alert/alertSlice';
import Modal from '../../components/Modal';
import { useModal } from '../../hooks/useModal';

const AddContact = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { modalConfig, showModal, closeModal } = useModal();
  const [loading, setLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    primaryRole: '',
    roles: [],
    company: '',
    contactInfo: {
      phone: '',
      email: '',
      instagram: ''
    },
    notes: ''
  });

  const roles = [
    'Agent', 'Backline Tech', 'FOH Engineer', 'Family', 'Guest', 'Industry', 
    'Lighting Designer', 'Manager', 'Merch Manager', 'Monitor Engineer', 
    'Opener', 'Photographer', 'Tour Manager', 'Support Act'
  ];

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleRoleChange = (role) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role]
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.name || !formData.primaryRole) {
      showModal('Required Fields', 'Name and primary role are required.', 'warning');
      return;
    }

    try {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      // Ensure primaryRole is in roles array
      const rolesToSave = [...new Set([...formData.roles, formData.primaryRole])];
      
      const dataToSave = {
        ...formData,
        roles: rolesToSave
      };

      console.log('Saving contact with data:', dataToSave);

      const response = await postDataAPI('user/contacts', dataToSave, token);

      if (response.data.status === 200 || response.data.status === 201) {
        showModal('Contact Created', 'Contact has been created successfully!', 'success');
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      } else {
        showModal('Error', response.data.message || 'Failed to create contact.', 'error');
      }
    } catch (error) {
      console.error('Error creating contact:', error);
      showModal('Error', error.response?.data?.message || 'Failed to create contact. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const basicFields = [
    { key: 'name', label: 'Name', placeholder: 'John Doe', required: true },
    { key: 'primaryRole', label: 'Primary Role', type: 'select', required: true, options: roles.map(role => ({ value: role, label: role })) },
    { key: 'company', label: 'Company', placeholder: 'Company name' },
    { key: 'contactInfo.email', label: 'Email', type: 'email', placeholder: 'email@example.com' },
    { key: 'contactInfo.phone', label: 'Phone', type: 'tel', placeholder: '+1 (555) 123-4567' },
    { key: 'contactInfo.instagram', label: 'Instagram', placeholder: '@username' },
    { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Additional notes...' }
  ];

  const getValue = (key) => {
    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      return formData[parent]?.[child] || '';
    }
    return formData[key] || '';
  };

  const renderField = (field) => {
    const value = getValue(field.key);

    if (field.type === 'select') {
      return (
        <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#af51de] hover:bg-[rgba(175,81,222,0.05)] transition-all">
          <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
            {field.label}
            {field.required && <span className="text-[#af51de] ml-1">*</span>}
          </label>
          <select
            value={value}
            onChange={(e) => handleChange(field.key, e.target.value)}
            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
          >
            <option value="" className="bg-black">Select a role</option>
            {field.options.map(option => (
              <option key={option.value} value={option.value} className="bg-black">
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#af51de] hover:bg-[rgba(175,81,222,0.05)] transition-all">
          <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
            {field.label}
            {field.required && <span className="text-[#af51de] ml-1">*</span>}
          </label>
          <textarea
            value={value}
            onChange={(e) => handleChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            rows={3}
            className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] resize-none"
          />
        </div>
      );
    }

    return (
      <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#af51de] hover:bg-[rgba(175,81,222,0.05)] transition-all">
        <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
          {field.label}
          {field.required && <span className="text-[#af51de] ml-1">*</span>}
        </label>
        <input
          type={field.type || 'text'}
          value={value}
          onChange={(e) => handleChange(field.key, e.target.value)}
          placeholder={field.placeholder}
          className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
        />
      </div>
    );
  };

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>New Contact</span>
            <div className="flex items-center gap-4">
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={handleSubmit}
              >
                Save
              </span>
              
              <span className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" onClick={() => navigate(-1)}>Go back</span>
            </div>
          </div>

          <div className="w-full px-4 flex-1 overflow-y-auto">
            <div className="flex flex-col">
              {/* Fields */}
              <div className="space-y-4 mb-6">
                {basicFields.map(renderField)}
              </div>

              {/* Additional Roles */}
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#af51de] hover:bg-[rgba(175,81,222,0.05)] transition-all mb-4">
                <label className="block text-white text-[12px] mb-3" style={{ fontWeight: 300 }}>
                  Additional Roles
                </label>
                <div className="flex flex-wrap gap-2">
                  {roles.slice(0, 12).map(role => (
                    <button
                      key={role}
                      onClick={() => handleRoleChange(role)}
                      className={`px-3 py-1 rounded text-[10px] font-medium transition-colors ${
                        formData.roles.includes(role)
                          ? 'bg-[#af51de] text-white'
                          : 'bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.7)] hover:bg-[rgba(255,255,255,0.15)]'
                      }`}
                    >
                      {role}
                    </button>
                  ))}
                </div>
              </div>

              {/* Save Button */}
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#af51de] hover:bg-[rgba(175,81,222,0.05)] transition-all">
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full bg-[#af51de] text-white text-[12px] font-medium py-3 rounded-lg hover:bg-[#9d40cd] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Creating Contact...
                    </>
                  ) : (
                    'Create Contact'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Modal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        showCancel={modalConfig.showCancel}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
      />
    </LayoutHeader>
  );
};

export default AddContact;
